import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchPendingStockTransferList,
  fetchPendingStockTransferDetails,
  createPendingStockTransfer,
  updatePendingStockTransfer,
  deletePendingStockTransfer,
} from "./thunk";

interface PendingStockTransferState {
  list: any[];
  rowCount: number;
  details: any | null;
  loading: boolean;
  error: string | null;
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
}

const initialState: PendingStockTransferState = {
  list: [],
  rowCount: 0,
  details: null,
  loading: false,
  error: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

const pendingStockTransferSlice = createSlice({
  name: "pst",
  initialState,
  reducers: {
    resetPSTState(state) {
      state.error = null;
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // 📌 LIST
      .addCase(fetchPendingStockTransferList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingStockTransferList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data;
        state.rowCount = action.payload.rowCount;
        state.error = null;
      })
      .addCase(fetchPendingStockTransferList.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed loading list";
      })

      // 📌 DETAILS
      .addCase(fetchPendingStockTransferDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingStockTransferDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.details = action.payload;
        state.error = null;
      })
      .addCase(fetchPendingStockTransferDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed fetching details";
      })

      // 📌 CREATE
      .addCase(createPendingStockTransfer.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPendingStockTransfer.fulfilled, (state) => {
        state.loading = false;
        state.createSuccess = true;
      })
      .addCase(createPendingStockTransfer.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed creating";
      })

      // 📌 UPDATE
      .addCase(updatePendingStockTransfer.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePendingStockTransfer.fulfilled, (state) => {
        state.loading = false;
        state.updateSuccess = true;
      })
      .addCase(updatePendingStockTransfer.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed updating";
      })

      // 📌 DELETE
      .addCase(deletePendingStockTransfer.pending, (state) => {
        state.loading = true;
      })
      .addCase(deletePendingStockTransfer.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.deleteSuccess = true;
        state.list = state.list.filter(
          (item) => item.transfer_id !== action.payload // <-- adjust id key as needed
        );
      })
      .addCase(deletePendingStockTransfer.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed deleting";
      });
  },
});

export const { resetPSTState } = pendingStockTransferSlice.actions;
export default pendingStockTransferSlice.reducer;
